import React, { useEffect, useState, useRef, useMemo } from 'react';
import PointageService from '../../components/PointageService';
import UserService from '../../components/UserService';
import Modal from '../../components/Modal';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  ButtonGroup,
  Chip,
  TextField,
  Card,
  CardContent,
  Typography,
  Stack,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ListIcon from '@mui/icons-material/List';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const direct = new Date(value);
    return Number.isNaN(direct.getTime()) ? null : direct;
  }

  if (typeof value === 'object') {
    if (value.$date) {
      const nested = new Date(value.$date);
      return Number.isNaN(nested.getTime()) ? null : nested;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
  }

  return null;
};

const isSameDay = (dateA, dateB) =>
  dateA.getFullYear() === dateB.getFullYear() &&
  dateA.getMonth() === dateB.getMonth() &&
  dateA.getDate() === dateB.getDate();

const formatDateTime = (value) => {
  const parsed = parseDateValue(value);
  return parsed ? parsed.toLocaleString() : '—';
};

const getCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMinutesDuration = (minutes) => {
  const value = Number(minutes || 0);
  const hours = Math.floor(value / 60);
  const mins = value % 60;
  return `${hours}h ${String(mins).padStart(2, '0')}`;
};

const escapeCsvValue = (value) => {
  const raw = value === null || typeof value === 'undefined' ? '' : String(value);
  return `"${raw.replace(/"/g, '""')}"`;
};

const normalizePointageList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.response)) return payload.response;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const sortPointagesDesc = (entries = []) =>
  entries
    .slice()
    .sort((a, b) => {
        const dateA = parseDateValue(a?.entree || a?.sortie);
        const dateB = parseDateValue(b?.entree || b?.sortie);
      const timeA = dateA ? dateA.getTime() : 0;
      const timeB = dateB ? dateB.getTime() : 0;
      return timeB - timeA;
    });

const getEntryUserId = (entry) => {
  if (!entry) return null;
  if (entry.userId) return entry.userId;
  if (entry.user_id) return entry.user_id;
  if (typeof entry.user === 'object' && entry.user !== null) {
    return entry.user._id || entry.user.id || entry.user.$oid || null;
  }
  if (typeof entry.user === 'string') return entry.user;
  if (entry.user && entry.user.$oid) return entry.user.$oid;
  return entry.user || null;
};

const ACTION_LABELS = {
  entree: "Pointer l'entrée",
  sortie: 'Pointer la sortie',
  pauseEntree: 'Début de pause',
  pauseSortie: 'Fin de pause',
};

const ACTION_DESCRIPTIONS = {
  entree: "Confirmez votre pointage d'entrée avec une capture photo.",
  sortie: "Confirmez votre pointage de sortie avec une capture photo.",
  pauseEntree: 'Démarrez votre pause lorsque vous êtes prêt.',
  pauseSortie: 'Validez la fin de votre pause pour reprendre.',
};

const ACTIONS_REQUIRING_PHOTO = new Set(['entree', 'sortie']);

function PointageView() {
  const userId = sessionStorage.getItem('userId');
  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const isAdmin = roles.includes('admin');
  const isManager = roles.includes('manager');
  const showPhoto = isAdmin || isManager;

  const [pointages, setPointages] = useState([]);
  const [users, setUsers] = useState([]);
  const [bulkRecords, setBulkRecords] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [modalImage, setModalImage] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchDate, setSearchDate] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [hoursFilter, setHoursFilter] = useState({ operator: '', value: '' });
  const [reportMonth, setReportMonth] = useState(getCurrentMonthValue);
  const [reportUserFilter, setReportUserFilter] = useState('');
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);
  const [monthlyReportError, setMonthlyReportError] = useState('');

  // responsive action/menu state (hooks must be top-level)
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget);
  const closeActionsMenu = () => setActionsAnchorEl(null);
  const [actionModal, setActionModal] = useState({ open: false, action: null });
  const [submittingAction, setSubmittingAction] = useState(false);
  const canFilterHours = isAdmin || isManager;

  const computeHoursWorked = (entry) => {
    const start = parseDateValue(entry?.entree);
    const end = entry?.sortie ? parseDateValue(entry.sortie) : null;
    if (!start) return null;
    if (!end) return 0;
    // if dates are not the same calendar day, do not compute hours
    if (!isSameDay(start, end)) return null;
    const diffMs = end.getTime() - start.getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return null;
    return diffMs / (1000 * 60 * 60);
  };

  const formatHoursWorked = (entry) => {
    const hours = computeHoursWorked(entry);
    return hours === null ? '—' : `${hours.toFixed(2)} h`;
  };

  const renderHoursBadge = (entry) => {
    const hours = computeHoursWorked(entry);

    if (hours === null) {
      return '—';
    }

    let color = 'default';

    if (hours < 7) {
      color = 'error';
    } else if (hours >= 8) {
      color = 'success';
    } else if (hours >= 7) {
      color = 'warning';
    }

    return (
      <Chip
        label={formatHoursWorked(entry)}
        color={color}
        size="small"
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const renderHoursBadgeValue = (hours) => {
    if (hours === null || typeof hours === 'undefined') return '—';
    let color = 'default';
    if (hours < 7) color = 'error';
    else if (hours >= 8) color = 'success';
    else if (hours >= 7) color = 'warning';
    const label = `${hours.toFixed(2)} h`;
    return (
      <Chip
        label={label}
        color={color}
        size="small"
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ fontWeight: 600, ml: 1 }}
      />
    );
  };

  const fetchData = async () => {
    if (isAdmin) {
      const data = await PointageService.getAllPointages();
      const normalized = normalizePointageList(data);
      setPointages(sortPointagesDesc(normalized));
    } else if (userId) {
      const data = await PointageService.getPointages(userId);
      const normalized = normalizePointageList(data);
      setPointages(sortPointagesDesc(normalized));
    }
    if (isAdmin || isManager) {
      const u = await UserService.fetchUsers();
      setUsers(u.response || u);
    } else if (userId) {
      const u = await UserService.fetchUser(userId);
      setUsers([u.response || u]);
    }
  };

  const fetchMonthlyReport = async () => {
    setMonthlyReportLoading(true);
    setMonthlyReportError('');
    try {
      const data = await PointageService.getMonthlyPointageReport({
        month: reportMonth,
        userId: reportUserFilter,
      });
      setMonthlyReport(data);
    } catch (error) {
      console.error('monthly pointage report', error);
      setMonthlyReportError(error?.response?.data?.error || 'Impossible de charger le rapport mensuel.');
    } finally {
      setMonthlyReportLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [pointages]);

  useEffect(() => {
    setPage(0);
  }, [searchDate]);

  useEffect(() => {
    setPage(0);
  }, [userFilter]);

  const filteredPointages = useMemo(() => {
    const byDate = (() => {
      if (!searchDate) {
        return pointages;
      }

      const [year, month, day] = searchDate.split('-').map(Number);
      if ([year, month, day].some(Number.isNaN)) {
        return pointages;
      }

      const targetDate = new Date(year, month - 1, day);

      return pointages.filter((entry) => {
        const parsed = parseDateValue(entry.entree);
        if (!parsed) {
          return false;
        }

        return isSameDay(parsed, targetDate);
      });
    })();

      // filter by user if selected
      if (userFilter) {
        return byDate.filter((entry) => {
          const entryUserId = getEntryUserId(entry);
          return entryUserId && String(entryUserId) === String(userFilter);
        });
      }

    if (!canFilterHours || !hoursFilter.operator || !hoursFilter.value) {
      return byDate;
    }

    const numericValue = parseFloat(hoursFilter.value);
    if (Number.isNaN(numericValue)) {
      return byDate;
    }

    return byDate.filter((entry) => {
      const hours = computeHoursWorked(entry);
      if (hours === null) return false;

      switch (hoursFilter.operator) {
        case 'gt':
          return hours > numericValue;
        case 'gte':
          return hours >= numericValue;
        case 'lt':
          return hours < numericValue;
        case 'lte':
          return hours <= numericValue;
        case 'eq':
          return hours === numericValue;
        default:
          return true;
      }
    });
  }, [pointages, searchDate, hoursFilter, canFilterHours, userFilter]);

  const paginatedPointages = useMemo(() => {
    if (rowsPerPage === -1) return filteredPointages;
    const start = page * rowsPerPage;
    return filteredPointages.slice(start, start + rowsPerPage);
  }, [filteredPointages, page, rowsPerPage]);

  const numberingOffset = rowsPerPage === -1 ? 0 : page * rowsPerPage;

  const handleSearchDateChange = (event) => {
    setSearchDate(event.target.value);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchMonthlyReport();
  }, [reportMonth, reportUserFilter]);

  const parseCsvContent = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length <= 1) return [];
    const rows = lines.slice(1);
    const records = [];
    for (const line of rows) {
      // remove starting single quote if present
      const cleaned = line.startsWith("'") ? line.slice(1) : line;
      // naive split by comma (assumes fields don't contain commas)
      const parts = cleaned.split(',');
      // map columns based on expected header
      const employee_id = (parts[0] || '').trim();
      const employee_name = (parts[1] || '').trim();
      const site = (parts[2] || '').trim();
      const timeRaw = (parts[3] || '').trim();
      // parse time like 2025-12-01 10:01:16 => dd/MM/yyyy HH:mm
      let datetime = timeRaw;
      try {
        const [datePart, timePart] = timeRaw.split(' ');
        if (datePart && timePart) {
          const [y, m, d] = datePart.split('-');
          const [hh, mm] = timePart.split(':');
          if (y && m && d && hh && mm) {
            datetime = `${d.padStart(2,'0')}/${m.padStart(2,'0')}/${y} ${hh.padStart(2,'0')}:${mm.padStart(2,'0')}`;
          }
        }
      } catch (e) {
        // keep raw if parsing fails
      }
      const attendanceCheckpoint = (parts[5] || '').trim();
      // split attendance checkpoint into location (first token) and device (rest)
      let location = '';
      let device = '';
      if (attendanceCheckpoint) {
        const tokens = attendanceCheckpoint.split(' ');
        location = tokens.shift() || '';
        device = tokens.join(' ').trim();
      }
      const unknown_field_2 = (parts[6] || '').trim();
      const record_type = (parts[7] || '').trim();

      records.push({
        employee_id,
        employee_name,
        site,
        datetime,
        unknown_field_1: null,
        location,
        device,
        unknown_field_2,
        record_type,
      });
    }

    return records;
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsvContent(text);
    setBulkRecords(parsed);
    setBulkResults(null);
  };

  const submitBulk = async () => {
    if (!bulkRecords || bulkRecords.length === 0) return;
    setBulkProcessing(true);
    try {
      const res = await PointageService.pointageBulk(bulkRecords);
      // Normalize response to expected shape { success, updated, created }
      if (res && typeof res === 'object' && ('success' in res || 'updated' in res || 'created' in res)) {
        setBulkResults({
          success: Boolean(res.success),
          updated: Number(res.updated || 0),
          created: Number(res.created || 0),
        });
      } else if (res && res.response && typeof res.response === 'object' && ('success' in res.response || 'updated' in res.response || 'created' in res.response)) {
        const r = res.response;
        setBulkResults({
          success: Boolean(r.success),
          updated: Number(r.updated || 0),
          created: Number(r.created || 0),
        });
      } else {
        setBulkResults(res);
      }
      // refresh pointages after upload
      await fetchData();
    } catch (err) {
      setBulkResults({ error: true, message: err?.message || 'Upload failed' });
    } finally {
      setBulkProcessing(false);
    }
  };

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      const videoElement = videoRef.current;
      if (typeof videoElement.pause === 'function') {
        videoElement.pause();
      }
      videoElement.srcObject = null;
    }
  };

  useEffect(() => {
    if (!actionModal.open) {
      stopCamera();
      return undefined;
    }

    let isCancelled = false;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (isCancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        mediaStreamRef.current = stream;
        const attachStream = () => {
          if (isCancelled) {
            return;
          }
          const videoElement = videoRef.current;
          if (!videoElement) {
            const raf =
              typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
                ? window.requestAnimationFrame
                : (cb) => setTimeout(cb, 16);
            raf(attachStream);
            return;
          }
          videoElement.srcObject = stream;
          const playPromise = videoElement.play?.();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              const handleLoaded = () => {
                videoElement.play?.().catch(() => {});
              };
              videoElement.addEventListener('loadedmetadata', handleLoaded, { once: true });
            });
          }
        };
        attachStream();
      } catch (error) {
        console.error('camera error', error);
      }
    };

    startCamera();

    return () => {
      isCancelled = true;
      stopCamera();
    };
  }, [actionModal.open]);

  useEffect(() => () => {
    stopCamera();
  }, []);

  useEffect(() => {
    if (actionModal.open && videoRef.current && mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
    }
  }, [actionModal.open]);

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !mediaStreamRef.current) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob), 'image/jpeg'));
  };

  const handleActionModalClose = () => {
    if (submittingAction) return;
    setActionModal({ open: false, action: null });
  };

  const triggerAction = (action) => {
    if (!userId) return;
    setActionModal({ open: true, action });
  };

  const handleActionConfirm = async () => {
    if (!actionModal.action || !userId) return;
    setSubmittingAction(true);
    try {
      if (ACTIONS_REQUIRING_PHOTO.has(actionModal.action)) {
        const img = await capturePhoto();
        if (!img) {
          alert("Impossible de capturer l'image");
          return;
        }
        if (actionModal.action === 'entree') {
          await PointageService.pointageEntree(userId, img);
        } else if (actionModal.action === 'sortie') {
          await PointageService.pointageSortie(userId, img);
        }
      } else if (actionModal.action === 'pauseEntree') {
        await PointageService.pointagePauseEntree(userId);
      } else if (actionModal.action === 'pauseSortie') {
        await PointageService.pointagePauseSortie(userId);
      }
      await fetchData();
      handleActionModalClose();
    } catch (error) {
      console.error('pointage action', error);
      alert('Une erreur est survenue pendant le pointage.');
    } finally {
      setSubmittingAction(false);
    }
  };

  const getUserName = (id) => {
    const u = users.find(user => String(user._id) === String(id));
    return u ? `${u.name} ${u.lastname}` : '';
  };

  const openImage = async (name) => {
    try {
      const blob = await PointageService.getPointageImage(name);
      const url = URL.createObjectURL(blob);
      setModalImage(url);
    } catch (e) {
      console.error('image fetch', e);
    }
  };

  const exportMonthlyReport = () => {
    const groups = Array.isArray(monthlyReport?.users) ? monthlyReport.users : [];
    if (!groups.length) return;

    const rows = [
      ['Mois', 'Utilisateur', 'Date', 'Entrée', 'Sortie', 'Pause', 'Temps net', 'Statut'],
    ];

    groups.forEach((group) => {
      const label = group.user?.label || getUserName(group.user?._id) || group.user?.username || group.user?._id || '';
      (group.records || []).forEach((record) => {
        rows.push([
          monthlyReport.month || reportMonth,
          label,
          record.date || '',
          record.entree ? formatDateTime(record.entree) : '',
          record.sortie ? formatDateTime(record.sortie) : '',
          formatMinutesDuration(record.pause_minutes),
          formatMinutesDuration(record.worked_minutes),
          record.status === 'complete' ? 'Complet' : 'Incomplet',
        ]);
      });
      rows.push([
        monthlyReport.month || reportMonth,
        `${label} - TOTAL`,
        '',
        '',
        '',
        formatMinutesDuration(group.total_pause_minutes),
        formatMinutesDuration(group.total_minutes),
        `${group.completed_days || 0} jour(s) complet(s), ${group.incomplete_days || 0} incomplet(s)`,
      ]);
    });

    const csv = rows.map(row => row.map(escapeCsvValue).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport-pointage-${monthlyReport.month || reportMonth}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const todaysPointage = useMemo(() => {
    if (!userId) return null;
    const today = new Date();
    return pointages.find((entry) => {
      const entryUserId = getEntryUserId(entry);
      if (!entryUserId || String(entryUserId) !== String(userId)) {
        return false;
      }
      const parsed = parseDateValue(entry.entree);
      return parsed ? isSameDay(parsed, today) : false;
    }) || null;
  }, [pointages, userId]);

  const todayCardData = useMemo(() => {
    return [
      {
        key: 'entree',
        title: 'Entrée',
        checked: Boolean(todaysPointage?.entree),
        timestamp: todaysPointage?.entree,
      },
      {
        key: 'sortie',
        title: 'Sortie',
        checked: Boolean(todaysPointage?.sortie),
        timestamp: todaysPointage?.sortie,
      },
      {
        key: 'pauseEntree',
        title: 'Pause début',
        checked: Boolean(todaysPointage?.pauseEntree),
        timestamp: todaysPointage?.pauseEntree,
      },
      {
        key: 'pauseSortie',
        title: 'Pause fin',
        checked: Boolean(todaysPointage?.pauseSortie),
        timestamp: todaysPointage?.pauseSortie,
      }
    ].map(card => ({
      ...card,
      statusText: card.checked ? 'Validé' : 'Non validé',
      subtitle: card.timestamp ? `Enregistré à ${formatDateTime(card.timestamp)}` : 'En attente',
    }));
  }, [todaysPointage]);

  // Build a map of YYYY-MM-DD -> array of user ids/names who pointed that day
  const pointagesByDay = useMemo(() => {
    const map = {};
    for (const p of pointages) {
      const date = parseDateValue(p.entree || p.sortie);
      if (!date) continue;
      const key = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
      const uid = getEntryUserId(p);
      const name = uid ? getUserName(uid) : p.employee_name || '';
      const hours = computeHoursWorked(p);
      if (!map[key]) map[key] = [];
      if (uid) {
        const existing = map[key].find(item => item.uid && String(item.uid) === String(uid));
        if (existing) {
          // keep the entry with the larger hours value if available
          if ((hours || 0) > (existing.hours || 0)) existing.hours = hours;
        } else {
          map[key].push({ uid, name, hours });
        }
      } else {
        const existing = map[key].find(item => !item.uid && item.name === name);
        if (!existing) map[key].push({ uid: null, name, hours });
      }
    }
    return map;
  }, [pointages, users]);

  const generateCalendarWeeks = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay(); // 0 (Sun) - 6 (Sat)

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let week = new Array(7).fill(null);
    let dayCounter = 1;

    // Fill first week
    for (let i = startDay; i < 7; i++) {
      week[i] = new Date(year, month, dayCounter++);
    }
    weeks.push(week);

    while (dayCounter <= daysInMonth) {
      week = new Array(7).fill(null);
      for (let i = 0; i < 7 && dayCounter <= daysInMonth; i++) {
        week[i] = new Date(year, month, dayCounter++);
      }
      weeks.push(week);
    }

    return weeks;
  };

  const goPrevMonth = () => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goNextMonth = () => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
        <h2 className="mb-0">Pointage</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <TextField
            label="Rechercher par date"
            type="date"
            size="small"
            value={searchDate}
            onChange={handleSearchDateChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Utilisateur"
            size="small"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Tous</MenuItem>
            {users.map((u) => (
              <MenuItem key={u._id || u.id || u} value={u._id || u.id || u}>
                {`${u.name || u.firstname || ''} ${u.lastname || ''}`.trim() || String(u._id || u.id || u)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => setViewMode(prev => prev === 'list' ? 'calendar' : 'list')}
            sx={{ ml: 1 }}
          >
            {viewMode === 'list' ? 'Vue Calendrier' : 'Vue Liste'}
          </Button>
        </div>
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            Importer CSV (Pointage)
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={submitBulk}
            disabled={!bulkRecords || bulkRecords.length === 0 || bulkProcessing}
          >
            {bulkProcessing ? 'Import en cours...' : `Envoyer (${bulkRecords.length || 0})`}
          </Button>
          {bulkResults && (
            <Typography variant="body2" sx={{ ml: 2 }}>
              {bulkResults.error ? (
                `Erreur: ${bulkResults.message}`
              ) : (bulkResults && typeof bulkResults === 'object' && 'success' in bulkResults) ? (
                `Succès: ${bulkResults.success ? 'oui' : 'non'} — Créés: ${bulkResults.created || 0} — Mis à jour: ${bulkResults.updated || 0}`
              ) : (
                `Résultats: ${JSON.stringify(bulkResults)}`
              )}
            </Typography>
          )}
        </div>
      )}

      {canFilterHours && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            label="Filtrer par heures"
            size="small"
            fullWidth
            value={hoursFilter.operator}
            onChange={(event) =>
              setHoursFilter((prev) => ({ ...prev, operator: event.target.value }))
            }
          >
            <MenuItem value="">Aucun filtre</MenuItem>
            <MenuItem value="gt">Supérieur à</MenuItem>
            <MenuItem value="gte">Supérieur ou égal à</MenuItem>
            <MenuItem value="lt">Inférieur à</MenuItem>
            <MenuItem value="lte">Inférieur ou égal à</MenuItem>
            <MenuItem value="eq">Égal à</MenuItem>
          </TextField>
          <TextField
            label="Heures"
            size="small"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.25 }}
            value={hoursFilter.value}
            onChange={(event) =>
              setHoursFilter((prev) => ({ ...prev, value: event.target.value }))
            }
            helperText="Basé sur la différence entrée/sortie"
          />
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          borderColor: '#dbeafe',
          background: 'linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)'
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Rapport mensuel par utilisateur
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calcul entre entrée, sortie et pauses, exportable par mois.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Mois"
              type="month"
              size="small"
              value={reportMonth}
              onChange={(event) => setReportMonth(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Utilisateur"
              size="small"
              value={reportUserFilter}
              onChange={(event) => setReportUserFilter(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Tous</MenuItem>
              {users.map((u) => (
                <MenuItem key={u._id || u.id || u} value={u._id || u.id || u}>
                  {`${u.name || u.firstname || ''} ${u.lastname || ''}`.trim() || String(u._id || u.id || u)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={fetchMonthlyReport}
              disabled={monthlyReportLoading}
            >
              {monthlyReportLoading ? 'Chargement...' : 'Actualiser'}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={exportMonthlyReport}
              disabled={!monthlyReport?.users?.length}
            >
              Exporter CSV
            </Button>
          </Stack>
        </Stack>

        {monthlyReportError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {monthlyReportError}
          </Typography>
        )}

        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }
          }}
        >
          {(monthlyReport?.users || []).map((group) => {
            const label = group.user?.label || getUserName(group.user?._id) || group.user?.username || group.user?._id;
            return (
              <Card key={group.user?._id || label} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {label || 'Utilisateur'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.records?.length || 0} pointage(s) sur {monthlyReport?.month || reportMonth}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={`Net ${formatMinutesDuration(group.total_minutes)}`} color="primary" />
                      <Chip label={`Pause ${formatMinutesDuration(group.total_pause_minutes)}`} variant="outlined" />
                      <Chip label={`${group.completed_days || 0} complet(s)`} color="success" variant="outlined" />
                      <Chip label={`${group.incomplete_days || 0} incomplet(s)`} color="warning" variant="outlined" />
                    </Stack>
                  </Stack>

                  <TableContainer sx={{ mt: 1.5, maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Entrée</TableCell>
                          <TableCell>Sortie</TableCell>
                          <TableCell>Pause</TableCell>
                          <TableCell>Net</TableCell>
                          <TableCell>Statut</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(group.records || []).map((record) => (
                          <TableRow key={record._id?.$oid || record._id || `${group.user?._id}-${record.date}`}>
                            <TableCell>{record.date || '—'}</TableCell>
                            <TableCell>{record.entree ? formatDateTime(record.entree) : '—'}</TableCell>
                            <TableCell>{record.sortie ? formatDateTime(record.sortie) : '—'}</TableCell>
                            <TableCell>{formatMinutesDuration(record.pause_minutes)}</TableCell>
                            <TableCell>{formatMinutesDuration(record.worked_minutes)}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.status === 'complete' ? 'Complet' : 'Incomplet'}
                                color={record.status === 'complete' ? 'success' : 'warning'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {!monthlyReportLoading && !monthlyReportError && !monthlyReport?.users?.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Aucun pointage trouvé pour ce mois.
          </Typography>
        )}
      </Paper>

      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }
        }}
      >
        {todayCardData.map((card) => (
          <Card key={card.key} variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {card.title}
              </Typography>
              <Typography
                variant="h6"
                color={card.checked ? 'success.main' : 'text.primary'}
                sx={{ fontWeight: 600 }}
              >
                {card.statusText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.subtitle}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          mt: 3
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ flexWrap: 'wrap' }}
        >
          {!isSmall ? (
            <ButtonGroup variant="contained" aria-label="pointage actions" sx={{ flexWrap: 'wrap' }}>
              <Button color="success" onClick={() => triggerAction('entree')} disabled={!userId}>Pointer l'entrée</Button>
              <Button color="error" onClick={() => triggerAction('sortie')} disabled={!userId}>Pointer la sortie</Button>
              <Button variant="outlined" color="warning" onClick={() => triggerAction('pauseEntree')} disabled={!userId}>Début de pause</Button>
              <Button variant="outlined" color="primary" onClick={() => triggerAction('pauseSortie')} disabled={!userId}>Fin de pause</Button>
            </ButtonGroup>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" color="success" onClick={() => triggerAction('entree')} disabled={!userId}>Pointer l'entrée</Button>
              <Button variant="outlined" onClick={openActionsMenu}>Actions</Button>
              <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={closeActionsMenu}>
                <MenuItem onClick={() => { closeActionsMenu(); triggerAction('sortie'); }} disabled={!userId}>Pointer la sortie</MenuItem>
                <MenuItem onClick={() => { closeActionsMenu(); triggerAction('pauseEntree'); }} disabled={!userId}>Début de pause</MenuItem>
                <MenuItem onClick={() => { closeActionsMenu(); triggerAction('pauseSortie'); }} disabled={!userId}>Fin de pause</MenuItem>
              </Menu>
            </div>
          )}
        </Stack>
        
      </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
          <IconButton aria-label="list view" color={viewMode === 'list' ? 'primary' : 'default'} onClick={() => setViewMode('list')}>
            <ListIcon />
          </IconButton>
          <IconButton aria-label="calendar view" color={viewMode === 'calendar' ? 'primary' : 'default'} onClick={() => setViewMode('calendar')}>
            <CalendarTodayIcon />
          </IconButton>
        </Box>

        {viewMode === 'list' ? (
        <TableContainer component={Paper} className="mt-3">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product #</TableCell>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Entrée</TableCell>
              <TableCell>Début de pause</TableCell>
              <TableCell>Fin de pause</TableCell>
              <TableCell>Sortie</TableCell>
              {canFilterHours && <TableCell>Heures travaillées</TableCell>}
              {showPhoto && (
                <>
                  <TableCell>Photo Entrée</TableCell>
                  <TableCell>Photo Sortie</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPointages.map((p, index) => (
              <TableRow key={p._id?.$oid || p._id || p.id} hover>
                <TableCell>{numberingOffset + index + 1}</TableCell>
                <TableCell>{getUserName(getEntryUserId(p))}</TableCell>
                <TableCell>{formatDateTime(p.entree)}</TableCell>
                <TableCell>{formatDateTime(p.pauseEntree)}</TableCell>
                <TableCell>{formatDateTime(p.pauseSortie)}</TableCell>
                <TableCell>{formatDateTime(p.sortie)}</TableCell>
                {canFilterHours && <TableCell>{renderHoursBadge(p)}</TableCell>}
                {showPhoto && (
                  <>
                    <TableCell>
                      {(p.imageEntree || p.image) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          data-bs-toggle="modal"
                          data-bs-target="#pointagePhoto"
                          onClick={() => openImage(p.imageEntree ?? p.image)}
                        >
                          Voir
                        </Button>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {(p.imageSortie || p.image) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          data-bs-toggle="modal"
                          data-bs-target="#pointagePhoto"
                          onClick={() => openImage(p.imageSortie ?? p.image)}
                        >
                          Voir
                        </Button>
                      ) : '—'}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredPointages.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
          labelRowsPerPage="Lignes par page"
        />
      </TableContainer>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <div>
              <Button size="small" onClick={goPrevMonth} sx={{ mr: 1 }}>Préc</Button>
              <Button size="small" onClick={goNextMonth}>Suiv</Button>
            </div>
            <Typography variant="h6">{calendarMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</Typography>
            <div />
          </Stack>
          <Paper variant="outlined">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, p: 1 }}>
              {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(d => (
                <Box key={d} sx={{ fontWeight: 700, textAlign: 'center' }}>{d}</Box>
              ))}
              {generateCalendarWeeks(calendarMonth).flat().map((date, idx) => {
                if (!date) return <Box key={idx} sx={{ border: '1px solid', minHeight: 100, p: 1, bgcolor: '#fafafa' }} />;
                const key = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
                const entries = pointagesByDay[key] || [];
                return (
                  <Box key={key} sx={{ border: '1px solid', minHeight: 100, p: 1, overflow: 'auto' }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{date.getDate()}</div>
                    <div style={{ marginTop: 6 }}>
                      {entries.length > 0 ? entries.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                          <div style={{ marginLeft: 8 }}>{renderHoursBadgeValue(item.hours)}</div>
                        </div>
                      )) : <div style={{ fontSize: 12, color: '#999' }}>—</div>}
                    </div>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={actionModal.open} onClose={handleActionModalClose} fullWidth maxWidth="sm">
        <DialogTitle>{ACTION_LABELS[actionModal.action] || 'Pointage'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ACTION_DESCRIPTIONS[actionModal.action] || ''}
          </Typography>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', borderRadius: 8, backgroundColor: '#000' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionModalClose} disabled={submittingAction}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleActionConfirm}
            disabled={submittingAction}
          >
            {submittingAction ? 'Validation...' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <Modal modalId="pointagePhoto" modalType="modal-lg" title="Photo de pointage">
        <div className="text-center">
          {modalImage && (
            <img src={modalImage} alt="pointage" className="img-fluid" />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default PointageView;
